/**
 * LUDOPRO - Configuration Pages Thématiques Produits
 * 
 * ARCHITECTURE HUB + LISTING:
 * - HUB: Pages marketing/navigation (bannières, cards de navigation)
 * - LISTING: Pages produits (Query Engine + State Engine)
 * 
 * RÈGLES:
 * - HUB: Aucun appel API produits, uniquement contenu éditorial
 * - LISTING: Uniquement produits, aucune bannière marketing
 */

// ============================================================================
// UTILITAIRES DE DATES
// ============================================================================

/**
 * Retourne la date actuelle au format ISO (YYYY-MM-DD)
 */
export const getTodayISO = () => {
  return new Date().toISOString().split('T')[0];
};

/**
 * Retourne une date relative à aujourd'hui au format ISO
 * @param {number} days - Nombre de jours (+/-)
 */
export const getRelativeDateISO = (days) => {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toISOString().split('T')[0];
};

// ============================================================================
// CONFIGURATIONS DES PAGES HUB (Marketing / Navigation)
// ============================================================================

export const HUB_CONFIGS = {
  
  // ========================================
  // HUB NOUVEAUTÉS
  // ========================================
  'nouveautes': {
    id: 'nouveautes',
    type: 'hub',
    title: 'Nouveautés',
    subtitle: 'Découvrez les dernières sorties et les prochaines nouveautés',
    banner: {
      enabled: true,
      images: [
        '/images/banners/nouveautes-1.jpg',
        '/images/banners/nouveautes-2.jpg'
      ],
      fallbackGradient: 'from-green-600 to-emerald-700'
    },
    navigation: [
      {
        id: 'en-cours',
        title: 'Nouveautés en cours',
        subtitle: 'Les sorties des 30 derniers jours',
        icon: 'Clock',
        link: '/dashboard/nouveautes/en-cours',
        color: 'bg-green-500/10 text-green-600 border-green-500/20'
      },
      {
        id: 'a-venir',
        title: 'Nouveautés à venir',
        subtitle: 'Les prochaines sorties',
        icon: 'Calendar',
        link: '/dashboard/nouveautes/a-venir',
        color: 'bg-blue-500/10 text-blue-600 border-blue-500/20'
      }
    ]
  },
  
  // ========================================
  // HUB PRÉCOMMANDES
  // ========================================
  'precommandes': {
    id: 'precommandes',
    type: 'hub',
    title: 'Pré-commandes',
    subtitle: 'Réservez dès maintenant les prochaines sorties',
    banner: {
      enabled: true,
      fallbackGradient: 'from-red-600 to-rose-700'
    },
    navigation: [
      {
        id: 'en-cours',
        title: 'Pré-commandes en cours',
        subtitle: 'Réservez maintenant',
        icon: 'ShoppingCart',
        link: '/dashboard/precommandes/en-cours',
        color: 'bg-red-500/10 text-red-600 border-red-500/20'
      },
      {
        id: 'a-venir',
        title: 'Pré-commandes à venir',
        subtitle: 'Bientôt disponibles',
        icon: 'Clock',
        link: '/dashboard/precommandes/a-venir',
        color: 'bg-orange-500/10 text-orange-600 border-orange-500/20'
      }
    ]
  },
  
  // ========================================
  // HUB OFFRES
  // ========================================
  'offres': {
    id: 'offres',
    type: 'hub',
    title: 'Offres Ponctuelles',
    subtitle: 'Promotions et remises exclusives',
    banner: {
      enabled: true,
      fallbackGradient: 'from-orange-600 to-amber-700'
    },
    navigation: [
      {
        id: 'en-cours',
        title: 'Promotions à la Référence',
        subtitle: 'Remises directes sur produits',
        icon: 'Tag',
        link: '/dashboard/offres/en-cours',
        color: 'bg-orange-500/10 text-orange-600 border-orange-500/20'
      }
    ]
  }
};

// ============================================================================
// CONFIGURATIONS DES PAGES LISTING (Produits)
// ============================================================================

export const LISTING_CONFIGS = {
  
  // ========================================
  // NOUVEAUTÉS EN COURS
  // ========================================
  'nouveautes-en-cours': {
    id: 'nouveautes-en-cours',
    type: 'listing',
    hubId: 'nouveautes',
    title: 'Nouveautés en cours',
    subtitle: 'Les sorties des 30 derniers jours',
    breadcrumb: [
      { label: 'Nouveautés', link: '/dashboard/nouveautes' },
      { label: 'En cours' }
    ],
    
    // ============================================================
    // LISTING ENGINE V2: Source Unique de Vérité Backend
    // ============================================================
    listingType: 'nouveautes_en_cours',
    
    // LEGACY fallback (si listingType non supporté)
    getFilters: () => ({}),
    
    sort: 'date_sortie_asc',
    
    ui: {
      showBadge: (product) => product.is_nouveaute === true,
      badgeLabel: 'Nouveauté',
      badgeColor: 'bg-green-100 text-green-800',
      emptyMessage: 'Aucune nouveauté en cours pour le moment',
      showDateSortie: true
    }
  },
  
  // ========================================
  // NOUVEAUTÉS À VENIR
  // ========================================
  'nouveautes-a-venir': {
    id: 'nouveautes-a-venir',
    type: 'listing',
    hubId: 'nouveautes',
    title: 'Nouveautés à venir',
    subtitle: 'Les prochaines sorties (30 prochains jours)',
    breadcrumb: [
      { label: 'Nouveautés', link: '/dashboard/nouveautes' },
      { label: 'À venir' }
    ],
    
    // ============================================================
    // LISTING ENGINE V2: Source Unique de Vérité Backend
    // ============================================================
    listingType: 'nouveautes_a_venir',
    
    getFilters: () => ({}),
    
    sort: 'date_sortie_asc',
    
    ui: {
      showBadge: (product) => product.is_nouveaute_a_venir === true,
      badgeLabel: 'À venir',
      badgeColor: 'bg-blue-100 text-blue-800',
      emptyMessage: 'Aucune nouveauté à venir pour le moment',
      showDateSortie: true
      // NOTE: showUnknownDate supprimé - géré par DateAvailabilityBadge sur ProductCard
    }
  },
  
  // ========================================
  // PRÉCOMMANDES EN COURS
  // ========================================
  'precommandes-en-cours': {
    id: 'precommandes-en-cours',
    type: 'listing',
    hubId: 'precommandes',
    title: 'Pré-commandes en cours',
    subtitle: 'Réservez dès maintenant',
    breadcrumb: [
      { label: 'Pré-commandes', link: '/dashboard/precommandes' },
      { label: 'En cours' }
    ],
    
    // ============================================================
    // LISTING ENGINE V2 - SOURCE UNIQUE DE VÉRITÉ: BACKEND
    // Utiliser listingType pour appeler /api/listings/{listingType}
    // Fallback: getFilters() si listingType non défini
    // ============================================================
    listingType: 'precommandes_en_cours',  // Listing Engine V2
    
    getFilters: () => ({}),
    
    sort: 'date_sortie_asc',
    
    ui: {
      showBadge: (product) => product.is_precommande_active === true,
      badgeLabel: 'Pré-commande',
      badgeColor: 'bg-red-100 text-red-800',
      emptyMessage: 'Aucune pré-commande en cours',
      showDateFin: true,
      showDateSortie: true
    }
  },
  
  // ========================================
  // PRÉCOMMANDES À VENIR
  // ========================================
  'precommandes-a-venir': {
    id: 'precommandes-a-venir',
    type: 'listing',
    hubId: 'precommandes',
    title: 'Pré-commandes à venir',
    subtitle: 'Bientôt ouvertes à la réservation',
    breadcrumb: [
      { label: 'Pré-commandes', link: '/dashboard/precommandes' },
      { label: 'À venir' }
    ],
    
    // ============================================================
    // LISTING ENGINE V2: Source Unique de Vérité Backend
    // ============================================================
    listingType: 'precommandes_a_venir',
    
    getFilters: () => ({}),
    
    sort: 'date_sortie_asc',
    
    ui: {
      showBadge: () => true,
      badgeLabel: 'Bientôt',
      badgeColor: 'bg-orange-100 text-orange-800',
      emptyMessage: 'Aucune pré-commande à venir',
      showDateSortie: true
    }
  },
  
  // ========================================
  // PROMOTIONS À LA RÉFÉRENCE (anciennement "Offres en cours")
  // ========================================
  'offres-en-cours': {
    id: 'offres-en-cours',
    type: 'listing',
    hubId: 'offres',
    title: 'Promotions à la Référence',
    subtitle: 'Remises directes sur produits',
    breadcrumb: [
      { label: 'Offres', link: '/dashboard/offres' },
      { label: 'Promotions à la Référence' }
    ],
    
    // ============================================================
    // LISTING ENGINE V2: Source Unique de Vérité Backend
    // ============================================================
    listingType: 'offres_en_cours',
    
    getFilters: () => ({}),
    
    sort: 'remise_desc',
    
    ui: {
      showBadge: (product) => product.is_offre === true,
      badgeLabel: 'Offre',
      badgeColor: 'bg-orange-100 text-orange-800',
      emptyMessage: 'Aucune promotion en cours',
      showRemise: true
    }
  },

  // ============================================================
  // SÉLECTION DU MOIS (Phase 3.4.3)
  // ============================================================
  'selection-du-mois': {
    id: 'selection-du-mois',
    type: 'listing',
    title: 'Sélection du Mois',
    subtitle: 'Notre sélection de jeux coup de cœur',
    breadcrumb: [
      { label: 'Sélection du Mois' }
    ],
    listingType: 'selection_mois',
    ui: {
      emptyMessage: 'Aucune sélection pour ce mois'
    }
  },

  // ============================================================
  // BEST-SELLERS (Phase 3.4.3)
  // ============================================================
  'bestsellers': {
    id: 'bestsellers',
    type: 'listing',
    title: 'Best-sellers',
    subtitle: 'Les jeux les plus populaires',
    breadcrumb: [
      { label: 'Best-sellers' }
    ],
    listingType: 'bestsellers',
    ui: {
      emptyMessage: 'Aucun best-seller pour le moment'
    }
  }
};

// ============================================================================
// CONFIGURATIONS LEGACY (Compatibilité avec ancien système)
// ============================================================================

export const PAGE_CONFIGS = {
  ...LISTING_CONFIGS,
  // Alias pour compatibilité
  'nouveautes': LISTING_CONFIGS['nouveautes-en-cours'],
  'precommandes': LISTING_CONFIGS['precommandes-en-cours'],
  'offres': LISTING_CONFIGS['offres-en-cours'],
  'selection': LISTING_CONFIGS['selection-du-mois'],
  'selection-du-mois': LISTING_CONFIGS['selection-du-mois'],
  'bestsellers': LISTING_CONFIGS['bestsellers']
};

// ============================================================================
// LISTE DES SLUGS HUB ET LISTING (pour routing)
// ============================================================================

/**
 * Retourne tous les slugs de pages HUB
 */
export const getHubSlugs = () => Object.keys(HUB_CONFIGS);

/**
 * Retourne tous les slugs de pages LISTING
 */
export const getListingSlugs = () => Object.keys(LISTING_CONFIGS);

/**
 * Vérifie si un slug correspond à un HUB
 */
export const isHubSlug = (slug) => {
  const normalized = slug?.replace(/^\/dashboard\//, '').split('/')[0];
  return !!HUB_CONFIGS[normalized];
};

/**
 * Vérifie si un chemin correspond à un LISTING
 * Ex: "/dashboard/nouveautes/en-cours" → true
 */
export const isListingPath = (path) => {
  const parts = path?.replace(/^\/dashboard\//, '').split('/');
  if (parts?.length >= 2) {
    const listingId = `${parts[0]}-${parts[1]}`;
    return !!LISTING_CONFIGS[listingId];
  }
  return false;
};

/**
 * Extrait le listingId depuis un chemin
 * Ex: "/dashboard/nouveautes/en-cours" → "nouveautes-en-cours"
 */
export const extractListingId = (path) => {
  const parts = path?.replace(/^\/dashboard\//, '').split('/');
  if (parts?.length >= 2) {
    return `${parts[0]}-${parts[1]}`;
  }
  return null;
};

// ============================================================================
// FONCTIONS D'ACCÈS
// ============================================================================

/**
 * Retourne la configuration d'une page HUB
 */
export const getHubConfig = (hubId) => {
  return HUB_CONFIGS[hubId] || null;
};

/**
 * Retourne la configuration d'une page LISTING
 */
export const getListingConfig = (listingId) => {
  // Normaliser l'ID
  const normalizedId = listingId
    .replace(/^\/dashboard\//, '')
    .replace(/\//g, '-')
    .toLowerCase();
  
  return LISTING_CONFIGS[normalizedId] || null;
};

/**
 * Retourne la configuration d'une page (HUB ou LISTING)
 * @deprecated Utiliser getHubConfig ou getListingConfig
 */
export const getPageConfig = (pageId) => {
  const normalizedId = pageId
    .replace('nouveautes-en-cours', 'nouveautes-en-cours')
    .replace(/^\//, '')
    .toLowerCase();
  
  return LISTING_CONFIGS[normalizedId] || PAGE_CONFIGS[normalizedId] || null;
};

/**
 * Retourne les filtres API pour une page LISTING
 */
export const getPageFilters = (pageId) => {
  const config = getListingConfig(pageId) || getPageConfig(pageId);
  if (!config || !config.getFilters) return {};
  return config.getFilters();
};

/**
 * Construit l'URL API avec les filtres
 */
export const buildPageApiUrl = (pageId, limit = 20) => {
  const filters = getPageFilters(pageId);
  const params = new URLSearchParams();
  
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      params.append(key, value);
    }
  });
  
  params.append('limit', limit);
  return `/products?${params.toString()}`;
};

/**
 * Retourne la configuration UI d'une page LISTING
 */
export const getPageUI = (pageId) => {
  const config = getListingConfig(pageId) || getPageConfig(pageId);
  return config?.ui || {};
};

// ============================================================================
// HELPER POUR ProductListBlock
// ============================================================================

export const mapCmsFilterToApiParams = (filter) => {
  const params = {};
  
  // Filtres classiques
  if (filter.tags) params.tags = filter.tags;
  if (filter.editeur) params.editeur = filter.editeur;
  if (filter.partner_id) params.partner_id = filter.partner_id;
  if (filter.type_jeu) params.type_jeu = filter.type_jeu;
  if (filter.search) params.search = filter.search;
  
  // Filtres dates ISO
  if (filter.date_sortie_gte) params.date_sortie_gte = filter.date_sortie_gte;
  if (filter.date_sortie_lte) params.date_sortie_lte = filter.date_sortie_lte;
  if (filter.include_null_date_sortie) params.include_null_date_sortie = filter.include_null_date_sortie;
  
  // Support page thématique via pageType
  if (filter.pageType) {
    const pageFilters = getPageFilters(filter.pageType);
    Object.assign(params, pageFilters);
  }
  
  return params;
};
